import { clampTo, hasImageInput } from '../../tools';
import type { KieModelConfig } from '../types';

export const openaiConfigs: Record<string, KieModelConfig> = {
    'gpt-image-2.5-flare': {
        // https://docs.kie.ai/market/gpt/gpt-image-2-5-flare-text-to-image
        // https://docs.kie.ai/market/gpt/gpt-image-2-5-flare-image-to-image
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'gpt-image-2-5-flare-image-to-image',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        // 图像 URL
                        input_urls: image_urls.slice(0, 16),
                        // 可选参数
                        background: clampTo(providerOptions?.background || 'auto', ['transparent', 'opaque', 'auto'] as const, 'auto'),
                    },
                };
            } else {
                return {
                    model: 'gpt-image-2-5-flare-text-to-image',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        // 可选参数
                        background: clampTo(providerOptions?.background || 'auto', ['transparent', 'opaque', 'auto'] as const, 'auto'),
                    },
                };
            }
        },
    },
    'gpt-image-2.5-sunburst': {
        // https://docs.kie.ai/market/gpt/gpt-image-2-5-sunburst-text-to-image
        // https://docs.kie.ai/market/gpt/gpt-image-2-5-sunburst-image-to-image
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'gpt-image-2-5-sunburst-image-to-image',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        // 图像 URL
                        input_urls: image_urls.slice(0, 16),
                        // 可选参数
                        background: clampTo(providerOptions?.background || 'auto', ['transparent', 'opaque', 'auto'] as const, 'auto'),
                    },
                };
            } else {
                return {
                    model: 'gpt-image-2-5-sunburst-text-to-image',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        // 可选参数
                        background: clampTo(providerOptions?.background || 'auto', ['transparent', 'opaque', 'auto'] as const, 'auto'),
                    },
                };
            }
        },
    },
    'gpt-image-2': {
        // https://docs.kie.ai/cn/market/gpt/gpt-image-2-text-to-image
        // https://docs.kie.ai/cn/market/gpt/gpt-image-2-image-to-image
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'gpt-image-2-image-to-image',
                    input: {
                        prompt: prompt.slice(0, 20000),
                        aspect_ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '16:9'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                        // 图像 URL
                        input_urls: image_urls.slice(0, 16),
                    },
                };
            }
            return {
                model: 'gpt-image-2-text-to-image',
                input: {
                    prompt: prompt.slice(0, 20000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '5:4', '4:5', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '16:9'),
                    resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K', '4K'] as const, '1K'),
                },
            };
        },
    },
    'gpt-image-1.5': {
        // https://docs.kie.ai/cn/market/gpt-image/1-5-text-to-image
        // https://docs.kie.ai/cn/market/gpt-image/1-5-image-to-image
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, size, aspectRatio } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'gpt-image/1.5-image-to-image',
                    input: {
                        prompt: prompt,
                        aspect_ratio: clampTo(aspectRatio || '1:1', ['3:2', '2:3', '1:1'] as const, '1:1'),
                        // 图片 URL
                        input_urls: image_urls.slice(0, 16),
                        // system
                        quality: 'medium',
                    },
                };
            }
            return {
                model: 'gpt-image/1.5-text-to-image',
                input: {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '1:1', ['3:2', '2:3', '1:1'] as const, '1:1'),
                    // system
                    quality: 'medium',
                },
            };
        },
    },
};
